/* eslint-disable max-lines */
import { WorkerRequirementStatus } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api";
import { DayOfTheWeek } from "@src/appV2/lib/dates/types";
import { startOfMonth } from "date-fns";
import { z } from "zod";

import { type Address, type Location } from "../../lib/GoogleMaps";
import { BAN_DISPUTE_STATUS, DISPUTE_REASON } from "../Placements/types/createBanDispute.schema";
import { TimeSlot } from "../Shift/Open/types";

export enum PlacementCandidateStatus {
  DISABLED = "disabled",
  ONBOARDING = "onboarding",
  PENDING = "pending",
  ACTIVATED = "activated",
}

export enum PlacementCandidateScreeningStatus {
  NO_SCREENING = "no_screening",
  PENDING = "pending",
  PASSED = "passed",
  PASSED_EXCEPTIONALLY = "passed:exceptionally",
  FAILED = "failed",
}

export enum JobType {
  FULL_TIME = "full_time",
  PART_TIME = "part_time",
  PER_DIEM = "per_diem",
  WEEKEND_WARRIOR = "weekend_warrior",
}

export function isJobType(jobType: string): jobType is JobType {
  return (
    jobType === JobType.FULL_TIME ||
    jobType === JobType.PART_TIME ||
    jobType === JobType.PER_DIEM ||
    jobType === JobType.WEEKEND_WARRIOR
  );
}

export enum ShiftNameType {
  AM = "am",
  PM = "pm",
  NOC = "noc",
}

export function asTimeSlot(shiftNameType: ShiftNameType): TimeSlot {
  switch (shiftNameType) {
    case ShiftNameType.AM: {
      return TimeSlot.AM;
    }

    case ShiftNameType.PM: {
      return TimeSlot.PM;
    }

    case ShiftNameType.NOC: {
      return TimeSlot.NOC;
    }

    default: {
      // causes a typescript compilation error if we missed a case above
      const exhaustiveCheck: never = shiftNameType;
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Unhandled time slot case: ${exhaustiveCheck}`);
    }
  }
}

export function isShiftNameType(shiftNameType: string): shiftNameType is ShiftNameType {
  return (
    shiftNameType === ShiftNameType.AM ||
    shiftNameType === ShiftNameType.PM ||
    shiftNameType === ShiftNameType.NOC
  );
}

export const placementGeoLocation = z.object({
  coordinates: z.tuple([z.number(), z.number()]),
  type: z.literal("Point"),
});

export type PlacementGeoLocation = z.infer<typeof placementGeoLocation>;

export const PlacementCandidateMarketingLinkClicksSchema = z.object({
  notYetActivated: z.number().optional(),
  notYetSubmittedApplication: z.number().optional(),
});

export type PlacementCandidateMarketingLinkClicks = z.infer<
  typeof PlacementCandidateMarketingLinkClicksSchema
>;

export interface PreferredCandidateArea {
  location?: PlacementGeoLocation;
  address: Address;
  distance: number;
}

export interface PreferredCandidateAreaForm {
  address: Address;
  location: Location;
  distance: number;
}

export const PLACEMENT_CANDIDATE_BAN_ACTION = {
  INTERVIEW_CANCELLED: "interview:cancelled",
  INTERVIEW_RESCHEDULED: "interview:rescheduled",
  INTERVIEW_NCNS: "interview:ncns",
} as const;
export type PlacementCandidateBanAction =
  (typeof PLACEMENT_CANDIDATE_BAN_ACTION)[keyof typeof PLACEMENT_CANDIDATE_BAN_ACTION];

// this include is being phased out, but still supplied by the backend
// for backwards compatibility with the mobile app. we leave it here to
// prevent zod schema validation errors  (unexpected 'type' value in union)
export const placementCandidateUploadedResumeSchema = z.object({
  type: z.literal("uploaded-resume"),
  id: z.string(),
  attributes: z.unknown(),
});

export type PlacementCandidateUploadedResume = z.infer<
  typeof placementCandidateUploadedResumeSchema
>;

export const placementCandidatesWorkerRequirementSchema = z.object({
  type: z.literal(API_TYPES.workerRequirement),
  id: z.string(),
  attributes: z.object({
    status: z.nativeEnum(WorkerRequirementStatus),
    expiry: z.string().optional(),
    isValid: z.boolean(),
    qualifications: z.array(z.string()),
    requiredByWorkplacesCount: z.number().optional(),
  }),
  relationships: z.object({
    requirement: z.object({
      data: z.object({
        type: z.literal(API_TYPES.requirement),
        id: z.string(),
      }),
    }),
  }),
});
export type PlacementCandidateIncludedWorkerRequirement = z.infer<
  typeof placementCandidatesWorkerRequirementSchema
>;

export const placementCandidateRequirementSchema = z.object({
  type: z.literal(API_TYPES.requirement),
  id: z.string(),
  attributes: z.object({
    name: z.string().optional(),
  }),
});
export type PlacementCandidateIncludedRequirement = z.infer<
  typeof placementCandidateRequirementSchema
>;

type PlacementCandidateWorkerRequirement =
  PlacementCandidateIncludedWorkerRequirement["attributes"] & {
    id: string;
    requirement: PlacementCandidateIncludedRequirement["attributes"] & {
      id: string;
    };
  };

export const placementCandidatePreferredBenefitsSchema = z.object({
  combined: z.boolean().optional(),
  healthInsurance: z.boolean().optional(),
  paidTimeOff: z.boolean().optional(),
});

export type PlacementCandidatePreferredBenefits = z.infer<
  typeof placementCandidatePreferredBenefitsSchema
>;

export interface SavedPayAndScheduleFormData {
  benefits?: PlacementCandidatePreferredBenefits;
  jobType?: JobType;
}

export interface EmptyPlacementCandidate {
  passportUrl: undefined;
}

export const placementCandidateWorkerAttributeIncludedSchema = z.object({
  id: z.string(),
  type: z.literal("workers"),
  attributes: z.object({
    name: z.string(),
    qualification: z.string(),
    distance: z.number().optional(),
    completedShiftsCount: z.number().optional(),
    cbhPassportDocuments: z.array(z.string()).optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    profileImageUrl: z.string().optional(),
  }),
});

export type PlacementCandidateWorkerAttributeIncluded = z.infer<
  typeof placementCandidateWorkerAttributeIncludedSchema
>;

export const placementCandidateIncludedBanSchema = z.object({
  type: z.literal(API_TYPES.placementCandidateBan),
  id: z.string(),
  attributes: z.object({
    reason: z.string(),
    expiresAt: z.string().datetime().optional(),
    revokedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    dispute: z
      .object({
        reason: z.nativeEnum(DISPUTE_REASON),
        description: z.string(),
        status: z.nativeEnum(BAN_DISPUTE_STATUS),
        resolution: z.string().optional(),
        resolvedAt: z.string().datetime().optional(),
      })
      .optional(),
  }),
});

export type PlacementCandidateIncludedBan = z.infer<typeof placementCandidateIncludedBanSchema>;

export const PLACEMENT_COHORT = {
  FLEX_REVIEWS_IN_JOBS: "flexReviewsInJobs",
  INTERVIEWS_ON_JOB_CARDS: "interviewsOnJobCards",
} as const;

export const BOOKING_REORDERING_COHORT = {
  ALREADY_SCREENED: "already_screened",
  BOOK_FIRST: "book_first",
  SCREEN_FIRST: "screen_first",
  SCREEN_LAST: "screen_last",
} as const;

export const bookingReorderingCohortSchema = z.enum([
  BOOKING_REORDERING_COHORT.ALREADY_SCREENED,
  BOOKING_REORDERING_COHORT.BOOK_FIRST,
  BOOKING_REORDERING_COHORT.SCREEN_FIRST,
  BOOKING_REORDERING_COHORT.SCREEN_LAST,
]);

export type BookingReorderingCohort = z.infer<typeof bookingReorderingCohortSchema>;

export type ActiveBookingReorderingCohort = Exclude<
  BookingReorderingCohort,
  typeof BOOKING_REORDERING_COHORT.ALREADY_SCREENED
>;

export const placementCandidateSchema = z.object({
  id: z.string(),
  type: z.literal("placement-candidates"),
  attributes: z.object({
    status: z.nativeEnum(PlacementCandidateStatus),
    screeningStatus: z.nativeEnum(PlacementCandidateScreeningStatus).optional(),
    preferredAreas: z
      .array(
        z.object({
          location: z.object({
            type: z.literal("Point"),
            coordinates: z.tuple([z.number(), z.number()]),
          }),
          address: z.object({
            city: z.string().optional(),
            country: z.string().optional(),
            countryCode: z.string().optional(),
            formatted: z.string().optional(),
            line1: z.string().optional(),
            postalCode: z.string().optional(),
            region: z.string().optional(),
            subpremise: z.string().optional(),
            state: z.string().optional(),
            stateCode: z.string().optional(),
            streetName: z.string().optional(),
            streetNumber: z.string().optional(),
            metropolitanStatisticalArea: z.string().optional(),
          }),
          distance: z.number(),
        })
      )
      .optional(),
    introductionUrl: z.string().optional(),
    resumeUrl: z.string().optional(),
    passportUrl: z.string().optional(),
    preferredPayRate: z
      .object({
        amountInMinorUnits: z.number(),
        currencyCode: z.string(),
      })
      .nullish(),
    currentYearsOfExperience: z.number().optional(),
    marketingLinkClicks: PlacementCandidateMarketingLinkClicksSchema.optional(),
    yearsOfExperienceByQualification: z.record(z.string(), z.number()).optional(),
    preferredDaysOfWeek: z.array(z.nativeEnum(DayOfTheWeek)).optional(),
    preferredStates: z.array(z.string()).optional(),
    payAndScheduleFormData: z
      .object({
        benefits: placementCandidatePreferredBenefitsSchema.optional(),
        jobType: z.nativeEnum(JobType).optional(),
      })
      .optional(),
    initialOnboardingFlowCompleted: z.boolean().optional(),
    bookingReorderingCohort: bookingReorderingCohortSchema.optional(),
    attendanceScore: z.number(),
  }),
  relationships: z.object({
    worker: z.object({
      data: z.object({
        id: z.string(),
        type: z.literal("workers"),
      }),
    }),
    workerRequirements: z
      .object({
        data: z.array(
          z.object({
            type: z.literal(API_TYPES.workerRequirement),
            id: z.string(),
          })
        ),
      })
      .optional(),
    mostRecentBan: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.placementCandidateBan),
          id: z.string(),
        }),
      })
      .optional(),
  }),
  meta: z
    .object({
      bansCountByAction: z
        .object({
          [PLACEMENT_CANDIDATE_BAN_ACTION.INTERVIEW_CANCELLED]: z.number(),
          [PLACEMENT_CANDIDATE_BAN_ACTION.INTERVIEW_RESCHEDULED]: z.number(),
          [PLACEMENT_CANDIDATE_BAN_ACTION.INTERVIEW_NCNS]: z.number(),
        })
        .optional(),
    })
    .optional(),
});

export const createCandidateResumeTimespanSchema = z
  .object({
    isCurrent: z.boolean().default(false),
    startDate: z.date().transform((date) => startOfMonth(date)),
    endDate: z
      .date()
      .optional()
      .transform((date) => (date ? startOfMonth(date) : undefined)),
  })
  .superRefine((data, context) => {
    if (!data.isCurrent && !isDefined(data.endDate)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date is required",
        path: ["endDate"],
      });
    } else if (!data.isCurrent && isDefined(data.endDate) && data.startDate > data.endDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Start date must be before end date",
        path: ["startDate"],
      });
    }
  })
  .transform((data) => {
    if (data.isCurrent) {
      return {
        ...data,
        endDate: undefined,
      };
    }

    return data;
  });

export const createCandidateResumeSchema = z.object({
  experience: z
    .array(
      z.object({
        roleName: z.string().min(1, "Role name is required").max(180, "Role name is too long"),
        workplaceName: z
          .string()
          .min(1, "Workplace name is required")
          .max(300, "Workplace name is too long"),
        timespan: createCandidateResumeTimespanSchema,
      })
    )
    .min(1, "At least one experience entry is required"),
  education: z
    .array(
      z.object({
        institutionName: z
          .string()
          .min(1, "Institution name is required")
          .max(180, "Institution name is too long"),
        educationType: z
          .string()
          .min(1, "Education type is required")
          .max(300, "Education type is too long"),
        timespan: createCandidateResumeTimespanSchema,
      })
    )
    .min(1, "At least one education entry is required"),
});

export type CreateCandidateResumeSchema = z.infer<typeof createCandidateResumeSchema>;

export interface PlacementCandidate {
  id: string;
  status: PlacementCandidateStatus;
  screeningStatus?: PlacementCandidateScreeningStatus;
  introductionUrl?: string;
  preferredAreas?: PreferredCandidateArea[];
  preferredPayRate?: {
    amountInMinorUnits: number;
    currencyCode: string;
  };
  currentYearsOfExperience?: number;
  worker: {
    id: string;
    name: string;
    qualification: string;
    distance?: number;
    completedShiftsCount?: number;
    cbhPassportDocuments?: string[];
    email?: string;
    phone?: string;
    profileImageUrl?: string;
  };
  passportUrl?: string;
  marketingLinkClicks?: PlacementCandidateMarketingLinkClicks;
  yearsOfExperienceByQualification?: Record<string, number>;
  preferredDaysOfWeek?: DayOfTheWeek[];
  preferredStates?: string[];
  payAndScheduleFormData?: SavedPayAndScheduleFormData;
  workerRequirements?: PlacementCandidateWorkerRequirement[];
  mostRecentBan?: PlacementCandidateIncludedBan["attributes"] & { id: string };
  isRestricted?: boolean;
  initialOnboardingFlowCompleted?: boolean;
  bansCountByAction?: Record<PlacementCandidateBanAction, number>;
  bookingReorderingCohort?: BookingReorderingCohort;
  attendanceScore: number;
}

// Type guard to distinguish between a real PlacementCandidate and an EmptyPlacementCandidate
export function isPlacementCandidate(
  candidate: PlacementCandidate | EmptyPlacementCandidate | undefined
): candidate is PlacementCandidate {
  return candidate !== undefined && "id" in candidate;
}

/*  eslint-enable max-lines */
